import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Calendar, TrendingUp, TrendingDown } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default function AdminAnalyticsPage() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [comparisonStartDate, setComparisonStartDate] = useState<string>("");
  const [comparisonEndDate, setComparisonEndDate] = useState<string>("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (token && startDate && endDate) {
      fetchAnalytics();
    }
  }, [token, startDate, endDate]);

  const fetchAnalytics = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await adminApi.getSalesAnalytics(startDate, endDate, token);
      setAnalytics(response);
    } catch (error: any) {
      console.error("Error fetching analytics:", error);
      toast.error(error.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('en-IN');
  };

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="h-64 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-4">
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const { 
    sales_over_time = [],
    total_sales_breakdown = {},
    sales_by_channel = [],
    sales_by_product = [],
    average_order_value_over_time = [],
    orders_over_time = [],
    summary = {}
  } = analytics;

  // Prepare data for charts
  const salesChartData = sales_over_time.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    current: item.total,
  }));

  const ordersChartData = orders_over_time.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    orders: item.count,
  }));

  const aovChartData = average_order_value_over_time.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    aov: item.avg_value,
  }));

  const channelChartData = sales_by_channel.map((item: any) => ({
    name: item.channel,
    value: item.total_amount,
    count: item.order_count,
  }));

  const productChartData = sales_by_product.slice(0, 5).map((item: any) => ({
    name: item.product_name.length > 30 ? item.product_name.substring(0, 30) + '...' : item.product_name,
    sales: item.total_sales,
    quantity: item.total_quantity,
  }));

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold font-playfair mb-1">Analytics</h1>
            <p className="text-sm text-gray-500">Last refreshed: {new Date().toLocaleTimeString()}</p>
          </div>
        </div>

        {/* Date Range Selectors */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Compare to:</span>
            <input
              type="date"
              value={comparisonStartDate}
              onChange={(e) => setComparisonStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span>to</span>
            <input
              type="date"
              value={comparisonEndDate}
              onChange={(e) => setComparisonEndDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Gross Sales Over Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Gross sales over time</h2>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary.total_gross_sales || 0)}
              </span>
              <span className="text-sm text-green-600 font-medium">+1.6K%</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={salesChartData}>
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0088FE" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0088FE" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
              <Tooltip 
                formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''}
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Area type="monotone" dataKey="current" stroke="#0088FE" fillOpacity={1} fill="url(#colorSales)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Total Sales Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <h2 className="text-base font-semibold text-gray-900 mb-4">Total sales breakdown</h2>
          <div className="space-y-2">
            {[
              { label: "Gross sales", value: total_sales_breakdown.gross_sales || 0, change: "+1.6K%" },
              { label: "Discounts", value: -(total_sales_breakdown.discounts || 0), change: "+1.6%" },
              { label: "Returns", value: -(total_sales_breakdown.returns || 0), change: "+968%" },
              { label: "Net sales", value: total_sales_breakdown.net_sales || 0, change: "+1.9K%" },
              { label: "Shipping charges", value: total_sales_breakdown.shipping_charges || 0, change: "+100%" },
              { label: "Return fees", value: total_sales_breakdown.return_fees || 0, change: "—" },
              { label: "Taxes", value: total_sales_breakdown.taxes || 0, change: "+301%" },
              { label: "Total sales", value: total_sales_breakdown.total_sales || 0, change: "+1.9K%", isTotal: true },
            ].map((item, index) => (
              <div key={index} className={`flex items-center justify-between py-1.5 ${item.isTotal ? 'border-t border-gray-200 pt-2 mt-1' : ''}`}>
                <span className={`text-sm ${item.isTotal ? 'font-semibold' : 'text-gray-700'}`}>{item.label}</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${item.isTotal ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                    {formatCurrency(Math.abs(item.value))}
                  </span>
                  <span className="text-xs text-gray-500">{item.change}</span>
                </div>
              </div>
            ))}
          </div>
            </motion.div>

        {/* Total Sales by Sales Channel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Total sales by sales channel</h2>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(total_sales_breakdown.total_sales || 0)}
              </span>
              <span className="text-sm text-green-600 font-medium">+1.7K%</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={channelChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => name && percent ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {channelChartData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-1">
            {channelChartData.map((item: any, index: number) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-gray-700">{item.name}</span>
                </div>
                <span className="font-medium text-gray-900">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Total Sales by Product */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <h2 className="text-base font-semibold text-gray-900 mb-4">Total sales by product</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={150} />
              <Tooltip 
                formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''}
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Bar dataKey="sales" fill="#0088FE" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Average Order Value Over Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Average order value over time</h2>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary.average_order_value || 0)}
              </span>
              <span className="text-sm text-green-600 font-medium">+14%</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={aovChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
              <Tooltip 
                formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''}
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Line type="monotone" dataKey="aov" stroke="#00C49F" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Orders Over Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Orders over time</h2>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">
                {formatNumber(summary.total_orders || 0)}
              </span>
              <span className="text-sm text-green-600 font-medium">+1.4K%</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={ordersChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number | undefined) => value !== undefined ? formatNumber(value) : ''}
                contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
              />
              <Line type="monotone" dataKey="orders" stroke="#FF8042" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
}
